import { TravelPreferenceSections } from '@marageti/z4-sdk/lib/people';

export type CategoryConfig = {
  key: keyof TravelPreferenceSections;
  label: string;
  defaultAsList?: boolean;
};

export const CATEGORIES: CategoryConfig[] = [
  { key: 'tripPlanning', label: 'Trip Planning', defaultAsList: true },
  { key: 'accommodations', label: 'Accommodations', defaultAsList: true },
  { key: 'flights', label: 'Flights', defaultAsList: true },
  { key: 'groundTransportation', label: 'Ground Transportation', defaultAsList: true },
  { key: 'dining', label: 'Dining', defaultAsList: true },
  { key: 'allergiesAndDietaryRestrictions', label: 'Allergies & Dietary Restrictions', defaultAsList: true },
  { key: 'healthConsiderations', label: 'Health Considerations', defaultAsList: true },
  { key: 'otherPreferences', label: 'Other Preferences', defaultAsList: false },
];

/**
 * Single source of truth for which travel-preference sub-keys exist. Derived
 * from `CATEGORIES` so adding a new section in one place automatically flows
 * to consumers (e.g. the AI suggestions hook).
 */
export const TRAVEL_PREFERENCE_KEYS: (keyof TravelPreferenceSections)[] = CATEGORIES.map((c) => c.key);

export const stripHtml = (html: string): string => {
  try {
    return new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';
  } catch {
    return html;
  }
};

export const buildPlainText = (
  categories: CategoryConfig[],
  travelPreferences: TravelPreferenceSections | undefined,
): string => categories
  .map(({ key, label }) => {
    const value = travelPreferences?.[key] as string | undefined;
    const text = value ? stripHtml(value).trim() : 'No preferences recorded';
    return `${label}:\n${text}`;
  })
  .join('\n\n');

export const buildHtmlText = (
  categories: CategoryConfig[],
  travelPreferences: TravelPreferenceSections | undefined,
): string => categories
  .map(({ key, label }) => {
    const value = travelPreferences?.[key] as string | undefined;
    const body = value ?? '<p><em>No preferences recorded</em></p>';
    return `<h3>${label}</h3>${body}`;
  })
  .join('');

export const copyRichText = async (html: string, plain: string): Promise<void> => {
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plain], { type: 'text/plain' }),
      }),
    ]);
  } catch {
    await navigator.clipboard.writeText(plain);
  }
};
